import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Card, Button } from 'flowbite-react';
import { FiPlus } from 'react-icons/fi';
import { useDatabase } from '../../hooks/useDatabase';
import { useResourceGroup } from '../../hooks/useResourceGroup';
import EditableTitle from '../../components/EditableTitle';

const ResourceGroupDetailPage: React.FC = () => {
  const { groupId, workspaceId } = useParams<{ groupId: string; workspaceId: string }>();
  const { group, loading, error } = useResourceGroup(groupId);
  const { applyCommand, database, safeData } = useDatabase();

  const [directResourceIds, setDirectResourceIds] = useState<string[]>([]);
  const [subgroupIds, setSubgroupIds] = useState<string[]>([]);
  const [selectedAdd, setSelectedAdd] = useState('');

  const teams = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }));
  }, [database]);

  const resources = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.resources()).map(([id, r]: [string, any]) => ({ id, name: r.name() }));
  }, [database]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!database || !groupId || !safeData) return;
      const groupObj = database.resource_groups()[groupId];
      if (!groupObj) return;
      const [r, g] = await Promise.all([
        groupObj.member_resource_ids(safeData),
        groupObj.member_group_ids(safeData),
      ]);
      if (!cancelled) {
        setDirectResourceIds(r as string[]);
        setSubgroupIds(g as string[]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [database, groupId, safeData]);

  const updateName = async (name: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'UpdateResourceGroup',
      id: groupId,
      actions: [{ type: 'SetName', name }],
    });
  };

  const updateTeam = async (teamId: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'UpdateResourceGroup',
      id: groupId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId || null }],
    });
  };

  const addMember = async () => {
    if (!groupId || !selectedAdd) return;
    await applyCommand({
      type: 'SetResourceGroupMembership',
      membership: {
        member: { type: 'Resource', value: selectedAdd },
        group: groupId,
      },
      enabled: true,
    });
    setSelectedAdd('');
  };

  const removeMember = async (resourceId: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'SetResourceGroupMembership',
      membership: {
        member: { type: 'Resource', value: resourceId },
        group: groupId,
      },
      enabled: false,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Resource group not found'}
      </div>
    );
  }

  const resourceNameById: Record<string, string> = {};
  for (const r of resources) resourceNameById[r.id] = r.name;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <EditableTitle title={group.name} onTitleChange={updateName} />

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div>
          <label className="text-sm text-cm-text-muted block mb-1">Managed by team</label>
          <select
            className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
            value={group.managedByTeamId || ''}
            onChange={(e) => updateTeam(e.target.value)}
          >
            <option value="">— none —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono font-medium text-cm-text-primary">Members</h3>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <select
            className="flex-1 bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
            value={selectedAdd}
            onChange={(e) => setSelectedAdd(e.target.value)}
          >
            <option value="">— select a resource to add —</option>
            {resources
              .filter((r) => !directResourceIds.includes(r.id))
              .map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
          </select>
          <Button size="xs" onClick={addMember} disabled={!selectedAdd}>
            <FiPlus className="mr-1" /> Add
          </Button>
        </div>

        {directResourceIds.length === 0 && subgroupIds.length === 0 ? (
          <p className="text-cm-text-muted text-sm font-mono py-4 text-center">
            No members yet.
          </p>
        ) : (
          <ul className="divide-y divide-cm-border-subtle">
            {directResourceIds.map((id) => (
              <li key={id} className="py-2 flex items-center justify-between">
                <Link
                  to={`/workspaces/${workspaceId}/resources/${id}`}
                  className="text-cm-accent-blue hover:underline"
                >
                  {resourceNameById[id] ?? id}
                </Link>
                <button
                  onClick={() => removeMember(id)}
                  className="text-xs text-cm-accent-red hover:underline"
                >
                  remove
                </button>
              </li>
            ))}
            {subgroupIds.map((id) => (
              <li key={id} className="py-2 flex items-center justify-between">
                <Link
                  to={`/workspaces/${workspaceId}/resource-groups/${id}`}
                  className="text-cm-accent-teal hover:underline"
                >
                  [group] {id}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default ResourceGroupDetailPage;
